/**
 * notificationCategories.js — what each notification category is called, who may switch it, and the
 * words of a payday reminder (#580 F-08 slice 5a, design D-6).
 *
 * **The server decides who may switch a category.** `GET /api/notification-prefs` marks each row
 * `eligible`, and `PUT` refuses a child's write to a parent-only category with a 403. This module
 * only hides what the server already refuses, so a child is never shown a switch that does nothing.
 * It fails closed: a row the server did not mark `eligible: true`, a category this client has no
 * label for, or a parent-only category for anyone who is not a parent, is not shown.
 *
 * **`paydayReminder` is parent-only.** It says a child's pay is being held, which is a parent's
 * decision about that child, never something a child is told through the bell.
 *
 * **Its first consumer is the Settings "Notifications" section** (owner decision d-20260928-12,
 * `components/NotificationSettings.jsx`), which draws its switches through `visiblePreferences`.
 * No screen shows a reminder yet: `paydayReminderText` has no caller until a bell or inbox exists.
 *
 * Keep the keys equal to `CATEGORIES` in `server/services/activityFeed.service.js`; the test beside
 * this file reads that list and fails when they differ. Keep `PARENT_ONLY_CATEGORIES` equal to the
 * server's `PARENT_ONLY` (`server/services/notification.service.js`); the server's
 * `notification-categories.contract.test.js` reads this file and fails when they differ.
 */

export const CATEGORY_LABELS = Object.freeze({
  questReview: 'Quests waiting for review',
  questUndone: 'A finished quest was undone',
  questAssigned: 'A quest was given to you',
  householdReset: 'The household was reset',
  paydayReminder: 'Payday held for later',
  goalCandle: 'A goal candle was lit',
});

export const PARENT_ONLY_CATEGORIES = Object.freeze(['questReview', 'householdReset', 'paydayReminder', 'goalCandle']);

/**
 * The preference rows a member may see and change, with their labels.
 *
 * @param {{prefs?: Array<{category: string, enabled: boolean, eligible: boolean}>}} response the prefs read
 * @param {{role?: string}} viewer the signed-in member
 * @returns {Array<{category: string, label: string, enabled: boolean}>}
 */
export function visiblePreferences(response, viewer) {
  const rows = Array.isArray(response?.prefs) ? response.prefs : [];
  const isParent = viewer?.role === 'admin';
  return rows
    .filter((row) => row && row.eligible === true && typeof row.enabled === 'boolean'
      && Object.prototype.hasOwnProperty.call(CATEGORY_LABELS, row.category)
      && (isParent || !PARENT_ONLY_CATEGORIES.includes(row.category)))
    .map((row) => ({ category: row.category, label: CATEGORY_LABELS[row.category], enabled: row.enabled }));
}

/**
 * The sentence for one payday reminder, or null when the notice cannot be read honestly.
 * It reads only the four fields the server publishes (child, amount, currency, due date).
 *
 * @param {{userId?: number, amount?: string, currency?: string, releaseAt?: string}} payload
 * @param {{childName?: string, timeZone?: string, locale?: string}} [options]
 * @returns {string|null}
 */
export function paydayReminderText(payload, { childName, timeZone, locale } = {}) {
  if (!payload || typeof payload !== 'object' || typeof payload.releaseAt !== 'string') return null;
  const amount = Number(payload.amount);
  const due = new Date(payload.releaseAt);
  if (!/^\d+\.\d{2}$/.test(String(payload.amount)) || !Number.isFinite(amount)
    || typeof payload.currency !== 'string' || !/^[A-Z]{3}$/.test(payload.currency)
    || Number.isNaN(due.getTime())) return null;
  try {
    const money = new Intl.NumberFormat(locale, { style: 'currency', currency: payload.currency }).format(amount);
    const day = new Intl.DateTimeFormat(locale, { weekday: 'short', month: 'short', day: 'numeric', timeZone }).format(due);
    const who = typeof childName === 'string' && childName.trim() ? `${childName.trim()}'s` : 'A child\'s';
    // "Due", not "will be paid": the date is when the money is owed, and a late release is possible.
    return `${who} pay of ${money} is held for later. It is due to be paid on ${day}.`;
  } catch {
    return null;
  }
}
